import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../utils/axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import RequireRole from '../components/auth/RequireRole';
import { landingPage } from '../config/permissions';
import Overview from './OverviewRedesigned';
import Devices from './Devices';
import DeviceDetail from './DeviceDetail';
import Users from './Users';
import UserDetail from './UserDetail';
import LiveEvents from './LiveEvents';
import HistorySync from './HistorySync';
import Migration from './Migration';
import Employees from './Employees';
import Sellers from './Sellers';
import { SystemLogs, NginxTraffic, ActivityProfilePage } from './logs';

const Dashboard = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [systemStats, setSystemStats] = useState(null);
  const [devices, setDevices] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [users, setUsers] = useState([]);
  const [eventSource, setEventSource] = useState(null);

  // Fetch users from database
  const fetchUsers = async () => {
    try {
      const response = await axios.get('/v1/users?limit=1000'); // Get all users
      if (response.data.success) {
        setUsers(response.data.data.users);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  useEffect(() => {
    if (!user) return;

    // Fetch users on mount
    fetchUsers();

    const connectSSE = () => {
      const token = localStorage.getItem('token');
      const sseUrl = import.meta.env.VITE_SSE_BASE_URL || 'http://localhost:3012/api/sse';
      const source = new EventSource(`${sseUrl}/events?token=${token}`);

      source.onopen = () => {
        console.log('SSE Connection established');
      };

      source.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleSSEEvent(data);
        } catch (err) {
          console.error('Error parsing SSE data:', err);
        }
      };

      source.onerror = (err) => {
        console.error('SSE Connection error:', err);
        if (source.readyState === EventSource.CLOSED) {
          setTimeout(() => {
            connectSSE();
          }, 5000);
        }
      };

      setEventSource(source);
    };

    connectSSE();

    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [user]);

  const handleSSEEvent = (data) => {
    // Add event to the events list
    const newEvent = {
      ...data,
      id: Date.now() + Math.random(),
    };
    
    // Keep the last 500 events, and drop anything older than an hour so a tab
    // left open all afternoon cannot show stale lines as though they were live.
    // An event with no usable timestamp is kept rather than thrown away.
    const oldestKept = Date.now() - 60 * 60 * 1000;
    setEvents(prev => [newEvent, ...prev]
      .filter(e => {
        const at = new Date(e.timestamp).getTime();
        return Number.isNaN(at) || at >= oldestKept;
      })
      .slice(0, 500));

    // Update specific data based on event type
    switch (data.type) {
      case 'system_status':
        setSystemStats(data.stats);
        if (data.stats?.devices) {
          // The snapshot lists exactly the dryers with an open link right now.
          // Keep the ones already on screen and mark them offline rather than
          // dropping them: a dryer that vanishes from this list leaves the
          // pages reading it with nothing to go on but what they believed when
          // they loaded, which is how a powered-off dryer kept showing green.
          setDevices(prev => {
            const connected = new Map(data.stats.devices.map(d => [d.serial, d]));
            const known = new Set(prev.map(d => d.serial));
            return [
              ...prev.map(d => connected.has(d.serial)
                ? { ...d, ...connected.get(d.serial), isOnline: true }
                : { ...d, isOnline: false, connectedAt: null }),
              ...data.stats.devices
                .filter(d => !known.has(d.serial))
                .map(d => ({ ...d, isOnline: true }))
            ];
          });
        }
        if (data.stats?.subscribers) {
          setSubscribers(data.stats.subscribers);
        }
        break;

      case 'device_connected':
        setDevices(prev => {
          const existing = prev.findIndex(d => d.serial === data.serial);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = {
              ...updated[existing],
              ...data,
              isOnline: true,
              connectedAt: data.timestamp,
              lastSeen: data.timestamp,
            };
            return updated;
          }
          return [...prev, {
            serial: data.serial,
            ip: data.ip,
            connectedAt: data.timestamp,
            lastSeen: data.timestamp,
            ...data,
            isOnline: true
          }];
        });
        break;

      case 'device_disconnected':
        setDevices(prev =>
          prev.map(d =>
            d.serial === data.serial
              ? { ...d, isOnline: false, lastSeen: data.timestamp, connectedAt: null }
              : d
          )
        );
        break;

      case 'subscriber_connected':
        setSubscribers(prev => {
          const existing = prev.findIndex(s => s.userId === data.userId);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = {
              ...updated[existing],
              ...data,
              socketCount: (updated[existing].socketCount || 0) + 1,
            };
            return updated;
          }
          return [...prev, data];
        });
        break;

      case 'subscriber_disconnected':
        setSubscribers(prev => 
          prev.map(s => 
            s.userId === data.userId 
              ? { ...s, socketCount: Math.max(0, (s.socketCount || 1) - 1) }
              : s
          ).filter(s => s.socketCount > 0)
        );
        break;

      case 'subscription_added':
        setSubscribers(prev => 
          prev.map(s => 
            s.userId === data.userId 
              ? { 
                  ...s, 
                  subscriptions: [...(s.subscriptions || []), { 
                    serial: data.serial, 
                    topic: data.topic 
                  }] 
                }
              : s
          )
        );
        break;

      // Alarm events
      case 'alarm_new':
      case 'alarm_update':
      case 'alarm_cleared':
        // Alarm events are already added to the events list at the top of this function
        // No additional state updates needed for alarms
        console.log(`Received ${data.type} event:`, data);
        break;

      // Handle other alarm-related events
      case 'alarm_alert':
      case 'alarm_alert_sent':
        console.log(`Received ${data.type} event:`, data);
        break;
    }
  };

  return (
    <DashboardLayout>
      <Routes>
        <Route path="overview" element={
          <RequireRole minRole="developer"><Overview systemStats={systemStats} eventHistory={events} /></RequireRole>
        } />
        <Route path="devices" element={
          <Devices devices={devices} subscribers={subscribers} users={users} />
        } />
        <Route path="devices/:serial" element={
          <DeviceDetail />
        } />
        <Route path="users" element={
          <Users users={users} subscribers={subscribers} />
        } />
        <Route path="users/:id" element={
          <UserDetail key={window.location.pathname} users={users} subscribers={subscribers} devices={devices} />
        } />
        <Route path="events" element={
          <RequireRole minRole="developer"><LiveEvents events={events} /></RequireRole>
        } />
        <Route path="history" element={
          <RequireRole minRole="developer"><HistorySync /></RequireRole>
        } />
        <Route path="migration" element={
          <RequireRole minRole="developer"><Migration /></RequireRole>
        } />
        <Route path="employees" element={
          <RequireRole minRole="admin"><Employees /></RequireRole>
        } />
        <Route path="vendors" element={
          <Sellers />
        } />
        {/* Logs routes - separate pages for System and Nginx */}
        <Route path="logs" element={<Navigate to="/dashboard/logs/system" replace />} />
        <Route path="logs/system" element={<RequireRole minRole="developer"><SystemLogs /></RequireRole>} />
        <Route path="logs/nginx" element={<RequireRole minRole="developer"><NginxTraffic /></RequireRole>} />
        <Route path="logs/activity/:entityType/:entityId" element={<RequireRole minRole="developer"><ActivityProfilePage /></RequireRole>} />
        <Route path="/" element={<Navigate to={`/dashboard/${landingPage(user?.role)}`} replace />} />
        {/* Anything else under /dashboard goes to the landing page rather than a blank screen */}
        <Route path="*" element={<Navigate to={`/dashboard/${landingPage(user?.role)}`} replace />} />
      </Routes>
    </DashboardLayout>
  );
};

export default Dashboard;
